import React from 'react';
import useRealtimeConnection from '../../hooks/useRealtimeConnection';
import { formatClock, formatDurationCompact } from '../../lib/formatters/time';
import StatusDot from './StatusDot';

// Effective status → StatusDot token (statuses vocabulary, tokens.css colors).
const STATUS_TO_TOKEN = {
  connected: 'running',
  stale: 'queued',
  connecting: 'queued',
  disconnected: 'failed',
  'no-transport': 'idle'
};

const STATUS_LABELS = {
  connected: 'Live',
  stale: 'Stale',
  connecting: 'Connecting',
  disconnected: 'Offline',
  'no-transport': 'No live feed'
};

/**
 * Realtime connection indicator (S5.3) — surfaces heartbeat staleness in the
 * TopBar. Derived status: No live feed / Live / Stale / Connecting / Offline
 * with last-heartbeat age, refreshed by useRealtimeConnection's 1s tick.
 *
 * Cutover §3: 'no-transport' is the honest boot state — no WS/SSE transport
 * exists in this build, so the indicator reads "No live feed" instead of the
 * fake "Live" the removed simulated heartbeat used to produce. Clicking it
 * offers no reconnect (there is nothing to reconnect TO yet), so the button
 * degrades to a passive pill in that state.
 */
function ConnectionIndicator() {
  const {
    connectionStatus,
    lastHeartbeat,
    heartbeatAgeMs,
    requestReconnect
  } = useRealtimeConnection();

  const token = STATUS_TO_TOKEN[connectionStatus] ?? 'idle';
  const label = STATUS_LABELS[connectionStatus] ?? connectionStatus;
  const lastHeartbeatTs = lastHeartbeat ? new Date(lastHeartbeat).getTime() : null;
  const ageLabel = lastHeartbeatTs === null
    ? 'no heartbeat yet'
    : `last heartbeat ${formatClock(lastHeartbeatTs)} (${formatDurationCompact(heartbeatAgeMs)} ago)`;

  // No transport: nothing to reconnect to — render a passive pill (no
  // click action) rather than a button that promises a reconnect it
  // cannot perform.
  if (connectionStatus === 'no-transport') {
    return (
      <span
        className="connection-indicator connection-indicator--no-transport"
        title={`No live data feed in this build — screens show live rows where a backend source exists, labeled samples elsewhere.`}
      >
        <StatusDot status={token} />
        <span className="connection-indicator__label">{label}</span>
      </span>
    );
  }

  return (
    <button
      className={`connection-indicator connection-indicator--${connectionStatus}`}
      onClick={requestReconnect}
      title={`${label} — ${ageLabel}. Click to reconnect.`}
      type="button"
    >
      <StatusDot status={token} pulse={connectionStatus === 'connected'} />
      <span className="connection-indicator__label">{label}</span>
      {connectionStatus !== 'connected' && (
        <span className="connection-indicator__age">
          {heartbeatAgeMs === null ? 'no heartbeat' : `${formatDurationCompact(heartbeatAgeMs)} ago`}
          {' · click to reconnect'}
        </span>
      )}
    </button>
  );
}

export default ConnectionIndicator;
